import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

import {
  CARD_CVC,
  CARD_DATE,
  CARD_NUMBER,
  CARD_POSTAL
} from 'payments/components/CardElements';
import { DEFAULT_CARD_FIELD_STATES } from 'payments/constants/card';
import { CLOVER_FIELD_TYPE_TO_FIELD_STATE_NAME } from 'payments/constants/clover';
import { loadPaymentProviderScript } from 'payments/helpers/loadPaymentProviderScript';
import { useCloverPayments } from 'payments/hooks/usePayments';
import { type CardFieldStates } from 'payments/types/card';
import {
  type Clover,
  CloverElementType,
  CloverElements,
  ImageStyles,
  InputPlaceholderStyles,
  InputStyles,
  TokenErrors
} from 'payments/types/clover';
import { CIRCULAR_STD } from 'reservations/brand/helpers/fonts';
import { isServer } from 'utility/isServer';

let clover: null | Clover = null;

const formatCloverError = (
  errorKey: CloverElementType,
  errorMessage: string
) => {
  if (!errorMessage) {
    return;
  }

  switch (errorKey) {
    case 'CARD_DATE':
      return errorMessage.replace('expiry', 'expiration date');
    case 'CARD_POSTAL_CODE':
      return errorMessage.replace('Card postal', 'Zip');
    default:
      return errorMessage;
  }
};

const getCardElementStyles = () => {
  const cardElements = [CARD_NUMBER, CARD_DATE, CARD_CVC, CARD_POSTAL];
  const styles: {
    [key: string]: InputStyles | InputPlaceholderStyles | ImageStyles;
  } = {};

  // These map to the selectors defined in the Clover iFrame styling docs
  // https://docs.clover.com/docs/customizing-iframe-elements-with-css
  cardElements.forEach(elementStyleSelector => {
    styles[`${elementStyleSelector} input`] = {
      fontSize: '16px',
      height: '23px',
      width: '100%',
      color: '#47558D',
      fontFamily: `${CIRCULAR_STD}, "Helvetica Neue", Arial, sans-serif`,
      backgroundColor: 'transparent'
    };
    styles[`${elementStyleSelector} input::placeholder`] = {
      color: 'transparent'
    };
    styles[`${elementStyleSelector} img`] = { display: 'none' };
  });

  return styles;
};

const createCardElements = (cloverElements: CloverElements) => {
  const styles = getCardElementStyles();

  const cardNumber = cloverElements.create('CARD_NUMBER', styles);
  const cardDate = cloverElements.create('CARD_DATE', styles);
  const cardCvv = cloverElements.create('CARD_CVV', styles);
  const cardPostalCode = cloverElements.create('CARD_POSTAL_CODE', styles);

  cardNumber.mount(`#${CARD_NUMBER}`);
  cardDate.mount(`#${CARD_DATE}`);
  cardCvv.mount(`#${CARD_CVC}`);
  cardPostalCode.mount(`#${CARD_POSTAL}`);

  return [cardNumber, cardDate, cardCvv, cardPostalCode];
};

const initClover = (
  cloverPlatformPublicKey: string,
  setCardFieldStates: Dispatch<SetStateAction<CardFieldStates>>
) => {
  // NOTE: Unable to resolve this TS error, as the new keywords makes it expect
  // a class constructor which we don't have.
  //@ts-expect-error
  clover = new window.Clover(cloverPlatformPublicKey);
  const cloverElements = clover?.elements();

  if (cloverElements) {
    const cardElements = createCardElements(cloverElements);
    cardElements.forEach(element => {
      const { elementType } = element;
      const fieldStateKey = CLOVER_FIELD_TYPE_TO_FIELD_STATE_NAME[elementType];

      element.addEventListener('blur', event => {
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          [fieldStateKey]: {
            ...currentFieldStates[fieldStateKey as keyof CardFieldStates],
            error: formatCloverError(
              elementType,
              event[elementType].error ?? ''
            )
          }
        }));
      });

      element.addEventListener('focus', () => {
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          [fieldStateKey]: {
            ...currentFieldStates[fieldStateKey as keyof CardFieldStates],
            isFocused: true
          }
        }));
      });
    });
  }
};

export const useClover = () => {
  const { cloverPaymentFormUrl, cloverPlatformPublicKey } = useCloverPayments();
  const [cardFieldStates, setCardFieldStates] = useState<CardFieldStates>(
    DEFAULT_CARD_FIELD_STATES
  );
  const [loadedClover, setLoadedClover] = useState(!!window.Clover);

  const onLoad = () => {
    setLoadedClover(true);
    initClover(cloverPlatformPublicKey, setCardFieldStates);
  };

  useEffect(() => {
    if (!isServer() && !loadedClover) {
      loadPaymentProviderScript(cloverPaymentFormUrl, onLoad);
    }
  }, []);

  // The clover elements can become unmounted if the page rerenders, this mounts them again
  useEffect(() => {
    const iframes = Array.from(document.querySelectorAll('iframe'));
    if (loadedClover && !iframes.some(el => el.title === 'CARD NUMBER')) {
      initClover(cloverPlatformPublicKey, setCardFieldStates);
    }
  });

  const handleCardSubmit = async () => {
    const cloverResult = await clover?.createToken();

    if (!cloverResult) {
      return {};
    }

    const { card, errors, token } = cloverResult;

    if (errors) {
      Object.entries(errors).forEach(([errorKey, errorMessage]) => {
        const fieldStateKey =
          CLOVER_FIELD_TYPE_TO_FIELD_STATE_NAME[errorKey as keyof TokenErrors];
        setCardFieldStates(currentFieldStates => ({
          ...currentFieldStates,
          [fieldStateKey]: {
            ...currentFieldStates[fieldStateKey as keyof CardFieldStates],
            error: formatCloverError(
              errorKey as CloverElementType,
              errorMessage
            )
          }
        }));
      });
      return { errors };
    }

    if (!token) {
      console.warn(
        'Clover Checkout - token not present in createToken response',
        cloverResult
      );
      return {};
    }
    return { card, token };
  };

  return {
    cardFieldStates,
    handleCardSubmit,
    setCardFieldStates
  };
};
